import { useState } from "react";
import client from "@/lib/api";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildCannibalizationMd } from "@/lib/mdBuilders";
import { toast } from "sonner";
import { Sparkles, Zap } from "lucide-react";
import { CreditActionButton } from "@/components/CreditActionButton";
import { MetricInfo } from "@/components/MetricInfo";

/**
 * Cannibalization scan is now user-triggered (iter34/35). Previously it
 * auto-ran on page load. The scan itself is currently free (mock data), but
 * the button pattern matches every other credit action so future real-SERP
 * integration can swap the cost without changing the UX.
 */
export default function Cannibalization() {
    const project = useProject();
    const [data, setData] = useState(null);

    const scan = async () => {
        if (!project) return;
        try {
            const r = await client.get(`/projects/${project.id}/cannibalization`);
            setData(r.data);
            const n = (r.data?.conflicts || []).length;
            toast.success(n === 0 ? "No cannibalization detected" : `Found ${n} conflict${n === 1 ? "" : "s"}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Scan failed");
        }
    };

    if (!project) return <div className="text-muted-foreground font-mono text-sm">loading…</div>;

    return (
        <div className="space-y-6" data-testid="cannibalization-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">The silent revenue leak · solved</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Cannibalization Detector</h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        Similarity analysis of pages competing for the same intent.
                        {data && ` Last scan: ${data.total_scanned} keywords.`}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <CreditActionButton
                        cost={0}
                        testid="run-cannib-scan-btn"
                        onConfirm={scan}
                        description="Analyzes tracked keywords for URL overlap and returns pages competing for the same intent. Scan is free while running on cached SERP data."
                    >
                        <span className="inline-flex items-center gap-1">
                            <Zap className="w-4 h-4" /> {data ? "Rescan" : "Run scan"}
                        </span>
                    </CreditActionButton>
                    <DownloadMdButton
                        filenamePrefix={`inphase-cannibalization-${project?.domain || "project"}`}
                        buildMarkdown={() => buildCannibalizationMd({ project, data })}
                        disabled={!data}
                        testId="download-cannib-md"
                    />
                </div>
            </div>

            {!data ? (
                <div className="border border-dashed border-border bg-card/30 p-10 text-center" data-testid="cannib-empty">
                    <Sparkles className="w-8 h-8 mx-auto text-primary mb-3" strokeWidth={1.5} />
                    <div className="text-lg">No scan yet</div>
                    <div className="text-sm text-muted-foreground mt-2">
                        Click <span className="text-foreground font-mono">Run scan</span> to detect pages competing for the same intent.
                    </div>
                </div>
            ) : data.conflicts.length === 0 ? (
                <div className="border border-border bg-card/40 p-10 text-center" data-testid="cannib-clean">
                    <Sparkles className="w-8 h-8 mx-auto text-success mb-3" strokeWidth={1.5} />
                    <div className="text-lg">No cannibalization detected.</div>
                    <div className="text-sm text-muted-foreground mt-2">Your topic clusters are clean.</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {data.conflicts.map((c, i) => (
                        <div key={i} className="border border-border bg-card/40 p-6" data-testid={`conflict-${i}`}>
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <div className="overline">Conflict</div>
                                    <div className="font-display text-xl mt-1">"{c.keyword}"</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-muted-foreground flex items-center gap-1 justify-end">Similarity <MetricInfo metricKey="cannibalization_similarity" /></div>
                                    <div className="font-mono text-xl text-warning">{(c.similarity * 100).toFixed(1)}%</div>
                                </div>
                            </div>
                            <div className="mt-4 space-y-2">
                                {c.urls.map((u, j) => (
                                    <div key={j} className="font-mono text-sm text-muted-foreground border-l-2 border-warning/50 pl-3">{u}</div>
                                ))}
                            </div>
                            <div className="mt-4 text-sm text-primary">{c.recommendation}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
